import React, { createContext, useContext, useState, useEffect } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      // Load from localStorage for guests
      const localCart = localStorage.getItem('guestCart');
      if (localCart) {
        setCart(JSON.parse(localCart));
      }
    }
  }, [isAuthenticated]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await cartAPI.get();
      setCart(response.data.cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1, variant = null) => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      return { success: false };
    }

    try {
      setLoading(true);
      const response = await cartAPI.add({ productId, quantity, variant });
      setCart(response.data.cart);
      toast.success('Added to cart');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add to cart';
      toast.error(message);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    try {
      setLoading(true);
      const response = await cartAPI.update({ itemId, quantity });
      setCart(response.data.cart);
      return { success: true };
    } catch (error) {
      toast.error('Failed to update cart');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      const response = await cartAPI.remove(itemId);
      setCart(response.data.cart);
      toast.success('Item removed from cart');
      return { success: true };
    } catch (error) {
      toast.error('Failed to remove item');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await cartAPI.clear();
      setCart({ items: [], subtotal: 0, total: 0 });
      return { success: true };
    } catch (error) {
      toast.error('Failed to clear cart');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async (code) => {
    try {
      setLoading(true);
      const response = await cartAPI.applyCoupon(code);
      setCart(response.data.cart);
      toast.success('Coupon applied successfully');
      return { success: true, discount: response.data.discount };
    } catch (error) {
      const message = error.response?.data?.message || 'Invalid coupon code';
      toast.error(message);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = async () => {
    try {
      const response = await cartAPI.removeCoupon();
      setCart(response.data.cart);
      toast.success('Coupon removed');
      return { success: true };
    } catch (error) {
      toast.error('Failed to remove coupon');
      return { success: false };
    }
  };

  const itemCount = cart.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;

  const value = {
    cart,
    cartItems: cart.items || [],
    cartTotal: cart.subtotal || 0,
    discount: cart.discount || 0,
    coupon: cart.coupon || null,
    loading,
    itemCount,
    fetchCart,
    addToCart,
    updateQuantity,
    updateCartItem: updateQuantity,
    removeFromCart,
    clearCart,
    applyCoupon,
    removeCoupon,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
